/**
 * LOOKING GLASS — Immich Archive API Client
 *
 * Thin fetch wrapper over the archive API. Contract (verified by audit):
 *
 *   GET /assets?q&project&tag&type&is_archived&page&limit
 *       -> { data: Asset[], meta: { total, page, limit, pages } }   (limit 1-100)
 *   GET /assets/:id
 *       -> { data: Asset & { tags[], projects[], ai_metadata } }
 *   GET /projects -> { data: [{ id, name, slug, description, cover_asset_id, is_archived }] }
 *   GET /tags     -> { data: [{ id, name, slug, type, color }] }
 *
 *   Asset = { id, path, filename, type, mime, size, hash, width, height,
 *             preview_path, thumb_path, created_at, modified_at,
 *             last_seen_at, is_archived, external_path, notes }
 *
 * Image bytes come from /assets/:id/preview and /assets/:id/thumb — NEVER from
 * preview_path / thumb_path, which are absolute local filesystem paths on the
 * API host and are meaningless to a browser.
 *
 * Every function resolves to a plain data object and NEVER throws; transport
 * failures come back as { ok:false, message } so the panel can render an
 * inline error instead of an unhandled rejection.
 */
import { resolveApiUrl, previewUrl, thumbUrl, loadImmichConfig } from '../utils/immichConfig.js';

export { previewUrl, thumbUrl };

const API_KEY_HEADER = 'x-api-key';

function buildHeaders(extra) {
  const { apiKey } = loadImmichConfig();
  const headers = { Accept: 'application/json', ...(extra || {}) };
  if (apiKey) headers[API_KEY_HEADER] = apiKey;
  return headers;
}

async function request(path, { method = 'GET', body, params, signal } = {}) {
  const url = resolveApiUrl(path) + buildQueryString(params);
  let resp;
  try {
    resp = await fetch(url, {
      method,
      signal,
      headers: buildHeaders(body ? { 'Content-Type': 'application/json' } : undefined),
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    if (err?.name === 'AbortError') return { ok: false, aborted: true, message: 'Request cancelled' };
    return { ok: false, status: 0, message: err?.message || String(err) };
  }

  if (resp.status === 204) return { ok: true, data: null };

  let payload = null;
  try {
    const text = await resp.text();
    payload = text ? JSON.parse(text) : null;
  } catch {
    return { ok: false, status: resp.status, message: `Malformed response from ${url} (${resp.status})` };
  }

  if (!resp.ok) {
    const detail = payload?.error?.message || payload?.error || payload?.message;
    return {
      ok: false,
      status: resp.status,
      message: `${resp.status}${resp.statusText ? ` ${resp.statusText}` : ''}${detail ? `: ${detail}` : ''}`,
    };
  }

  // Tolerate both `{data:[...]}` and a bare array/object body.
  return { ok: true, data: payload?.data ?? payload, meta: payload?.meta ?? null, payload };
}

function buildQueryString(params) {
  if (!params) return '';
  const parts = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }
  return parts.length ? `?${parts.join('&')}` : '';
}

/** Clamp limit into the server-accepted 1-100 window. */
function clampLimit(limit) {
  const n = Number.parseInt(limit, 10);
  if (!Number.isFinite(n)) return 48;
  return Math.min(100, Math.max(1, n));
}

/**
 * GET /assets — paginated, filterable asset list.
 * @returns { ok, data: Asset[], meta: { total, page, limit, pages } }
 */
export async function listAssets({
  q = '', project = '', tag = '', type = '', isArchived = null,
  page = 1, limit = 48, signal,
} = {}) {
  const params = {
    q: q || undefined,
    project: project || undefined,
    tag: tag || undefined,
    type: type || undefined,
    is_archived: isArchived === null || isArchived === undefined ? undefined : (isArchived ? 'true' : 'false'),
    page: Math.max(1, Number.parseInt(page, 10) || 1),
    limit: clampLimit(limit),
  };
  const res = await request('/assets', { params, signal });
  if (!res.ok) return { ok: false, status: res.status, message: res.message, data: [], meta: null };
  return {
    ok: true,
    data: Array.isArray(res.data) ? res.data : [],
    meta: res.meta || { total: Array.isArray(res.data) ? res.data.length : 0, page: params.page, limit: params.limit, pages: 1 },
  };
}

/** GET /assets/:id — full detail including tags, projects, ai_metadata. */
export async function getAsset(id, { signal } = {}) {
  if (!id) return { ok: false, message: 'No asset id' };
  const res = await request(`/assets/${encodeURIComponent(id)}`, { signal });
  if (!res.ok) return { ok: false, status: res.status, message: res.message, data: null };
  return { ok: true, data: res.data };
}

/** GET /projects */
export async function listProjects({ signal } = {}) {
  const res = await request('/projects', { signal });
  if (!res.ok) return { ok: false, status: res.status, message: res.message, data: [] };
  return { ok: true, data: Array.isArray(res.data) ? res.data : [] };
}

/** GET /tags */
export async function listTags({ signal } = {}) {
  const res = await request('/tags', { signal });
  if (!res.ok) return { ok: false, status: res.status, message: res.message, data: [] };
  return { ok: true, data: Array.isArray(res.data) ? res.data : [] };
}

// ── Presentation helpers (pure, no network) ─────────────────────────────────

export function assetTitle(asset) {
  if (!asset) return 'Untitled';
  return asset.filename || asset.path?.split('/').pop() || 'Untitled';
}

export function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  let v = n;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i += 1; }
  return `${v >= 10 || i === 0 ? Math.round(v) : v.toFixed(1)} ${units[i]}`;
}

export function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const IMAGE_MIME_RE = /^image\//i;

export function isImageAsset(asset) {
  if (!asset) return false;
  if (IMAGE_MIME_RE.test(asset.mime || '')) return true;
  // Fall back to the extension when the archive has no MIME recorded.
  const name = asset.filename || asset.path || '';
  return /\.(jpe?g|png|gif|webp|avif|bmp|svg)$/i.test(name);
}

/**
 * Choose the best local card image source for an asset.
 * Images get the preview route; everything else (video/audio/pdf) falls back to
 * null so the card renders an icon tile instead of a broken <img>.
 */
export function cardImageFor(asset) {
  if (!isImageAsset(asset)) return null;
  return previewUrl(asset);
}